import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  locationDetailingProductPath,
  locationDetailingProductsPath,
  locationDetailingProductStockAdjustmentsPath,
} from "@/routes";
import { quantity, type Product } from "./types";

export default function AdjustmentForm({
  product,
  kind,
}: {
  product: Product;
  kind: "receipt" | "correction" | "threshold";
}) {
  const { auth, errors } = usePage().props;
  const { t, i18n } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(
    kind === "threshold" ? "inventory.threshold_action" : `inventory.${kind}`,
  );
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("inventory.title"),
          href: locationDetailingProductsPath(locationId),
        },
        {
          title: product.name,
          href: locationDetailingProductPath(locationId, product.id),
        },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <h1 className="text-page font-display">{title}</h1>
        <p className="font-medium break-words">{product.name}</p>
        <p className="text-sm">
          {t("inventory.balance")}: {quantity(product.quantity, i18n.language)}{" "}
          {t(`inventory.${product.unit}`)}
        </p>
        <p className="text-muted-foreground text-sm">
          {t(`inventory.${kind}_help`)}
        </p>
        <Card>
          <CardContent className="pt-6">
            <Form
              key={`${kind}:${product.balance_revision}`}
              action={locationDetailingProductStockAdjustmentsPath(
                locationId,
                product.id,
              )}
              method="post"
            >
              {({ processing }) => (
                <div className="space-y-4">
                  {Object.keys(errors || {}).length > 0 && (
                    <div role="alert" className="text-destructive text-sm">
                      {Object.values(errors).map((error, i) => (
                        <p key={i}>{error}</p>
                      ))}
                    </div>
                  )}
                  <input
                    type="hidden"
                    name="revision"
                    value={product.balance_revision}
                  />
                  <input
                    type="hidden"
                    name="stock_adjustment[kind]"
                    value={kind}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="stock-quantity">
                      {t(
                        kind === "threshold"
                          ? "inventory.threshold"
                          : kind === "correction"
                            ? "inventory.count"
                            : "inventory.quantity",
                      )}{" "}
                      ({t(`inventory.${product.unit}`)})
                    </Label>
                    <Input
                      autoFocus
                      id="stock-quantity"
                      name="stock_adjustment[quantity]"
                      inputMode={
                        product.unit === "litre" ? "decimal" : "numeric"
                      }
                      defaultValue={
                        kind === "correction"
                          ? product.quantity
                          : kind === "threshold"
                            ? product.threshold || ""
                            : ""
                      }
                      required={kind !== "threshold"}
                      aria-invalid={!!errors?.quantity}
                      aria-describedby="quantity-help"
                    />
                    <p
                      id="quantity-help"
                      className="text-muted-foreground text-sm"
                    >
                      {t(`inventory.precision_${product.unit}`)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stock-reason">
                      {t("inventory.reason")}
                    </Label>
                    <Textarea
                      id="stock-reason"
                      name="stock_adjustment[reason]"
                      required
                      maxLength={500}
                      aria-invalid={!!errors?.reason}
                      aria-describedby="reason-help"
                    />
                    <p
                      id="reason-help"
                      className="text-muted-foreground text-sm"
                    >
                      {t("inventory.reason_help")}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("inventory.save")}
                    </Button>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={locationDetailingProductPath(
                            locationId,
                            product.id,
                          )}
                        />
                      }
                    >
                      {t("inventory.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
